import { fromUnixTime } from "date-fns/esm";
import cronsTrue from "cronstrue";
import { useCallback } from "react";

import KeyValue from "components/KeyValue";
import { Cross, Tick } from "components/icons/generated";
import Badge from "ds/components/Badge";
import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import LinkButton from "ds/components/Button/LinkButton";
import CardCollapsibleWrapper from "ds/components/Card/Collapsible/components/Wrapper";
import CardCollapsibleHeader from "ds/components/Card/Collapsible/components/Header";
import CardCollapsibleContent from "ds/components/Card/Collapsible/components/Content";
import CardContent from "ds/components/Card/components/Content";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import FeedbackActions from "ds/components/Feedback/Actions";
import Icon from "ds/components/Icon";
import Typography from "ds/components/Typography";
import { AnalyticsPageStack } from "hooks/useAnalytics/pages/stack";
import { useToggle } from "hooks/useToggle";
import useTypedContext from "hooks/useTypedContext";
import { SCHEDULING_TYPE } from "shared/Stack/Scheduling/types";
import useStackScheduledRunDelete from "shared/Stack/Scheduling/useStackScheduledRunDelete";
import { ScheduledRun } from "types/generated";
import { getDocsUrl } from "utils/getDocsUrl";
import { StackContext } from "views/Stack/Context";

import ScheduledDateDisplay from "../components/ScheduledDateDisplay";
import { StackSchedulingContextApi } from "../Context";
import { getNextSchedule } from "../helpers";
import { showManageScheduledRunConfirmationModal } from "./ConfirmationModal";

const REFETCH_QUERIES = ["GetStackScheduling"];

type StackManageScheduledRunListItemProps = {
  integration: ScheduledRun;
  hasPrivateWorkerPool: boolean;
};

const StackManageScheduledRunListItem = ({
  integration,
  hasPrivateWorkerPool,
}: StackManageScheduledRunListItemProps) => {
  const [isCollapsed, toggleState] = useToggle(false);
  const toggle = useCallback(() => toggleState(), [toggleState]);
  const { stack, canManageStackAndRuns } = useTypedContext(StackContext);
  const { onEdit } = useTypedContext(StackSchedulingContextApi);

  const { deleteScheduledRun, loading } = useStackScheduledRunDelete({
    refetchQueries: REFETCH_QUERIES,
  });

  const onDelete = useCallback(() => {
    deleteScheduledRun(stack.id, integration.id);
  }, [deleteScheduledRun, stack.id, integration.id]);

  const handleConfirmDelete = useCallback(() => {
    showManageScheduledRunConfirmationModal({
      onConfirm: onDelete,
    });
  }, [onDelete]);

  const handleEdit = useCallback(() => onEdit(integration), [onEdit, integration]);

  const isRecurring = !!integration.cronSchedule;
  const isFinished = !integration.nextSchedule && !isRecurring;
  const isBlocked = !hasPrivateWorkerPool;

  return (
    <CardCollapsibleWrapper isCollapsed={isCollapsed} onToggle={toggle}>
      <CardCollapsibleHeader
        ariaLevel={3}
        ariaLabel="Run"
        suffix={
          <>
            {isFinished && (
              <Badge state="neutral" size="small">
                FINISHED
              </Badge>
            )}

            {canManageStackAndRuns.result && (
              <DropdownMenuEllipsis tooltip="Scheduled run actions" buttonVariant="ghost">
                <DropdownMenuItem onAction={handleEdit}>Edit</DropdownMenuItem>
                <DropdownMenuItem
                  onAction={handleConfirmDelete}
                  loading={loading}
                  danger
                  analyticsPage={AnalyticsPageStack.StackScheduling}
                  analyticsTitle="Schedule Delete Clicked"
                  analyticsProps={{ type: SCHEDULING_TYPE.RUN }}
                >
                  Delete
                </DropdownMenuItem>
              </DropdownMenuEllipsis>
            )}
          </>
        }
      >
        Run
      </CardCollapsibleHeader>

      {isBlocked && (
        <CardContent hasHeader>
          <Banner
            variant="danger"
            title="Scheduled run for a given stack is not scheduling:"
            fullWidth
          >
            The stack uses an unsupported public worker pool.
            <FeedbackActions>
              <LinkButton
                variant="secondary"
                size="small"
                href={getDocsUrl("/concepts/stack/scheduling")}
                rel="noopener noreferrer"
                target="_blank"
              >
                Read more
              </LinkButton>
            </FeedbackActions>
          </Banner>
        </CardContent>
      )}

      <CardCollapsibleContent hasHeader>
        <KeyValue name="Name">{integration.name}</KeyValue>

        {!isBlocked && isRecurring && integration.nextSchedule && (
          <KeyValue name="Starts in">{getNextSchedule(integration.nextSchedule)}</KeyValue>
        )}

        {integration.timestampSchedule && (
          <KeyValue name="Scheduled date">
            <ScheduledDateDisplay date={fromUnixTime(integration.timestampSchedule)} />
          </KeyValue>
        )}

        {integration.cronSchedule && (
          <KeyValue name="Scheduled">
            <Box direction="column">
              {integration.cronSchedule.map((item, i, { length }) => (
                <Typography key={`${item}.${i}`} variant="p-body3" tag="span">
                  {length > 1 && " •"} {cronsTrue.toString(item.trim())}
                </Typography>
              ))}
            </Box>
          </KeyValue>
        )}

        {integration.timezone && <KeyValue name="Timezone">{integration.timezone}</KeyValue>}

        <KeyValue name="Custom runtime config">
          {integration.customRuntimeConfig?.yaml ? (
            <Icon src={Tick} color="success" />
          ) : (
            <Icon src={Cross} color="danger" />
          )}
        </KeyValue>
      </CardCollapsibleContent>
    </CardCollapsibleWrapper>
  );
};

export default StackManageScheduledRunListItem;
